import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, NgModel } from '@angular/forms';
import { Observable } from 'rxjs';
import { FormValidationService } from '../../../services/form-validation.service';
import { Utils } from '../../../services/utils.service';


const noop = () => {
};


export const GENERIC_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => GenericInputComponent),
  multi: true
};


let nextUniqueId = 0;

// More reading: https://github.com/angular/material2/blob/master/src/lib/input/input.ts
// More reading: http://almerosteyn.com/2016/04/linkup-custom-control-to-ngcontrol-ngmodel
@Component({
  selector: 'ld-input-generic, ld-textarea',
  templateUrl: './ld-input-generic.template.html',
  providers: [GENERIC_INPUT_CONTROL_VALUE_ACCESSOR],
  encapsulation: ViewEncapsulation.None
})
export class GenericInputComponent implements ControlValueAccessor, OnChanges {
  public title: string = '';
  public counter: number = nextUniqueId++;
  public _valid: boolean = true;

  @ViewChild('input') input: ElementRef;
  @ViewChild('inputModel') inputModel: NgModel;

  private _focused: boolean = false;
  private _value: any = '';
  public elementType: 'input' | 'textarea';

  private _onTouchedCallback: () => void = noop;
  private _onChangeCallback: (_: any) => void = noop;

  /** Readonly properties. */
  get empty() {
    return (this._value == null || this._value === '') && this.type !== 'date';
  }

  get inputName(): string {
    return `${this.name}${this.counter}`;
  }

  /**
   * Misc
   */
  @Input() labelPrefix: string;
  @Input() label: string;
  @Input() defaultTitle: string = '';
  @Input() hasValidationError: boolean = false;
  @Input() errorTitleType: string = null;

  /**
   * Bindings.
   */
  @Input() max: string | number = null;
  @Input() maxlength: number = null;
  @Input() min: string | number = null;
  @Input() minlength: number = null;
  @Input() pattern: string = null;
  @Input() placeholder: string = null;
  @Input() step: number = null;
  @Input() type: string = 'text';
  @Input() name: string = null;
  @Input() mask: string = null;

  @HostBinding('class') _class = 'd-block';

  private _autofocus: boolean = false;
  private _disabled: boolean = false;
  private _required: boolean = false;
  private _spellcheck: boolean = false;
  _readonly: boolean = false;

  @Input()
  get autofocus(): boolean {
    return this._autofocus;
  }

  set autofocus(value) {
    this._autofocus = this.coerceBooleanProperty(value);
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value) {
    this._disabled = this.coerceBooleanProperty(value);
  }

  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(value) {
    this._required = this.coerceBooleanProperty(value);
  }

  @Input()
  get spellcheck(): boolean {
    return this._spellcheck;
  }

  set spellcheck(value) {
    this._spellcheck = this.coerceBooleanProperty(value);
  }

  @Input()
  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(value) {
    this._readonly = this.coerceBooleanProperty(value);
  }

  private _blurEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  private _focusEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

  @Output('blur')
  get onBlur(): Observable<FocusEvent> {
    return this._blurEmitter.asObservable();
  }

  @Output('focus')
  get onFocus(): Observable<FocusEvent> {
    return this._focusEmitter.asObservable();
  }

  get value(): any {
    return this._value;
  };

  @Input() set value(v: any) {
    v = this._convertValueForInputType(v);
    if (v !== this._value) {
      this._value = v;
      this._onChangeCallback(v);
    }
  }


  constructor(elementRef: ElementRef,
              public formValidation: FormValidationService,
              private changeDetectorRef: ChangeDetectorRef,
              private utils: Utils) {
    this.elementType = elementRef.nativeElement.nodeName.toLowerCase() === 'ld-textarea' ? 'textarea' : 'input';

    setTimeout(() => {
      changeDetectorRef.markForCheck();
    }, 0);
  }

  ngOnChanges() {
    this.updateTitle();
  }

  _handleFocus(event: FocusEvent) {
    this._focused = true;
    this._focusEmitter.emit(event);
  }

  _handleBlur(event: FocusEvent) {
    this._focused = false;
    this._onTouchedCallback();
    this._blurEmitter.emit(event);
  }

  _handleChange(event: Event) {
    this.value = (<HTMLInputElement>event.target).value;
    this._onTouchedCallback();
    this.updateTitle();
  }

  _handleKeyPress(event: KeyboardEvent): boolean | void {
    // prevent the letter 'e' from being entered in number fields
    if (this.type === 'number' && event.key.toLowerCase() === 'e') {
      event.preventDefault();
      return false;
    } else if (this.pattern === this.formValidation.regex.phone) {
      this.value = this.utils.formatPhoneNumber(this.value);
    } else if (this.pattern === this.formValidation.regex.phoneWithExt) {
      this.value = this.utils.formatPhoneNumberWithExt(this.value);
    } else if (this.pattern === this.formValidation.regex.ssn) {
      this.value = this.utils.formatSSN(this.value);
    }
    return;
  }

  writeValue(value: any) {
    this._value = value;
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: any) {
    this._onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this._onTouchedCallback = fn;
  }

  updateTitle() {
    if (this.required && this.empty) {
      this.title = this.formValidation.errors.required + '\n\n' + this.defaultTitle;
    } else if (this.valid()) {
      this.title = this.defaultTitle;
    } else {
      let errorTitle: string = this.formValidation.errorTitle(this.inputModel, this.errorTitleType);

      if (errorTitle !== '' && this.defaultTitle !== '') {
        errorTitle = errorTitle + '\n\n'; // separate errors if there are two
      }
      this.title = errorTitle + this.defaultTitle;
    }
  }

  /**
   * Add custom validation state so dates aren't incorrectly flagged
   */
  valid(): boolean {
    if (this.required && this.empty) {
      return false;
    } else if (this.type === 'date' && this.value === null && !this.required) {
      return true;
    } else if (this.inputModel !== null && typeof this.inputModel !== 'undefined') {
      return this.inputModel.valid;
    }

    return true;
  }

  coerceBooleanProperty(value: any): boolean {
    return value != null && `${value}` !== 'false';
  }

  /**
   * Convert the value passed in to a value that is expected from the type of the md-input.
   * This is normally performed by the *_VALUE_ACCESSOR in forms, but since the type is bound
   * on our internal input it won't work locally.
   * @private
   */
  private _convertValueForInputType(v: any): any {
    let returnValue = v;
    switch (this.type) {
      case 'number':
        returnValue = this.utils.convertNumberValue(v);
        break;
      default:
        break;
    }

    return returnValue;
  }


}
